/**
 * AVA Design System — HeroPanel
 *
 * Panel con el gradient de marca + 3 capas de waves SVG + opcional
 * particles pulsantes. Es la ÚNICA superficie con decorative motion
 * en el sistema; usar SOLO en Login (panel izquierdo) y Home (banner).
 *
 * No usar en cards, modales, ni backgrounds genéricos.
 */
import type { ReactNode } from "react";

interface HeroPanelProps {
  children: ReactNode;
  className?: string;
  /** Mostrar las 20 partículas blancas pulsantes. Default true. */
  particles?: boolean;
  /** Mostrar las 3 ondas SVG decorativas. Default true. */
  waves?: boolean;
}

// Posiciones pseudo-aleatorias pero determinísticas para SSR-safety.
// Si necesitás más densidad o variación, expandí este array.
const PARTICLES: ReadonlyArray<{ x: number; y: number; size: number; delay: number; duration: number }> = [
  { x: 8,  y: 12, size: 2, delay: 0,   duration: 3 },
  { x: 22, y: 28, size: 3, delay: 0.5, duration: 4 },
  { x: 41, y: 8,  size: 2, delay: 1,   duration: 3.5 },
  { x: 58, y: 18, size: 4, delay: 0.2, duration: 5 },
  { x: 71, y: 35, size: 2, delay: 0.8, duration: 3 },
  { x: 88, y: 22, size: 3, delay: 1.3, duration: 4 },
  { x: 14, y: 52, size: 2, delay: 0.6, duration: 3.2 },
  { x: 33, y: 68, size: 4, delay: 1.1, duration: 4.5 },
  { x: 52, y: 48, size: 2, delay: 0.3, duration: 3 },
  { x: 67, y: 72, size: 3, delay: 1.5, duration: 5 },
  { x: 81, y: 58, size: 2, delay: 0.9, duration: 3.8 },
  { x: 95, y: 78, size: 3, delay: 0.4, duration: 4 },
  { x: 5,  y: 88, size: 2, delay: 1.2, duration: 3.5 },
  { x: 28, y: 92, size: 4, delay: 0.7, duration: 4.2 },
  { x: 47, y: 82, size: 2, delay: 1.4, duration: 3 },
  { x: 62, y: 95, size: 3, delay: 0.1, duration: 5 },
  { x: 76, y: 88, size: 2, delay: 1.0, duration: 3.6 },
  { x: 89, y: 8,  size: 3, delay: 0.5, duration: 4 },
  { x: 38, y: 40, size: 2, delay: 1.6, duration: 3.4 },
  { x: 19, y: 76, size: 3, delay: 0.0, duration: 4.8 },
];

export function HeroPanel({ children, className, particles = true, waves = true }: HeroPanelProps) {
  return (
    <div
      className={`relative overflow-hidden ava-hero-gradient text-white ${className ?? ""}`}
    >
      {waves && (
        <svg
          className="pointer-events-none absolute inset-0 w-full h-full"
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <path
            d="M0,600 Q300,500 600,580 T1200,560 L1200,800 L0,800 Z"
            fill="rgba(255, 255, 255, 0.15)"
          />
          <path
            d="M0,650 Q400,560 800,640 T1200,620 L1200,800 L0,800 Z"
            fill="rgba(255, 255, 255, 0.1)"
          />
          <path
            d="M0,700 Q500,640 1000,690 T1200,680 L1200,800 L0,800 Z"
            fill="rgba(255, 255, 255, 0.08)"
          />
        </svg>
      )}

      {particles && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {PARTICLES.map((p, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-white animate-pulse"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                opacity: 0.6,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10">{children}</div>
    </div>
  );
}
